// src/components/admin/scheduling/useAdminScheduleHotkeys.js
import { useEffect } from "react";

export default function useAdminScheduleHotkeys({
  enabled,
  onCopy,
  onPaste,
  onDelete,
  onClearSelection,
  onArrowLeft,
  onArrowRight,
}) {
  useEffect(() => {
    if (!enabled) return;

    function isTypingTarget(el) {
      const tag = el?.tagName?.toLowerCase?.();
      return tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable;
    }

    function isArrowNavigationBlocked(el) {
      if (!el) return false;
      if (isTypingTarget(el)) return true;
      if (typeof el.closest !== "function") return false;

      return !!el.closest(
        [
          "[role='textbox']",
          "[role='combobox']",
          "[role='listbox']",
          "[role='option']",
          "[contenteditable='true']",
        ].join(",")
      );
    }

    function onKeyDown(e) {
      // Don't hijack hotkeys while typing in inputs/modals
      if (isTypingTarget(document.activeElement)) return;

      const ctrl = e.ctrlKey || e.metaKey;

      // Undo selection
      if (e.key === "Escape") {
        onClearSelection?.();
        return;
      }

      // Copy
      if (ctrl && (e.key === "c" || e.key === "C")) {
        e.preventDefault();
        onCopy?.();
        return;
      }

      // Paste
      if (ctrl && (e.key === "v" || e.key === "V")) {
        e.preventDefault();
        onPaste?.();
        return;
      }

      // Delete
      if (e.key === "Delete" || e.key === "Backspace") {
        onDelete?.();
        return;
      }

      if (e.key === "ArrowLeft") {
        if (isArrowNavigationBlocked(e.target) || isArrowNavigationBlocked(document.activeElement)) return;
        if (!onArrowLeft) return;
        e.preventDefault();
        onArrowLeft();
        return;
      }

      if (e.key === "ArrowRight") {
        if (isArrowNavigationBlocked(e.target) || isArrowNavigationBlocked(document.activeElement)) return;
        if (!onArrowRight) return;
        e.preventDefault();
        onArrowRight();
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [enabled, onCopy, onPaste, onDelete, onClearSelection, onArrowLeft, onArrowRight]);
}
